import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useTranslation } from '../../i18n';
import {
    FaSearch,
    FaTags,
    FaPlus,
    FaTimes,
    FaSync,
    FaCog,
    FaNewspaper,
    FaExternalLinkAlt,
    FaChevronDown,
    FaChevronUp,
    FaCheck,
    FaInfoCircle,
    FaIndustry,
    FaSave,
    FaTrash,
    FaClipboardList,
    FaCalendarAlt,
    FaGlobe,
    FaStar,
    FaRegStar,
    FaVideo,
    FaHeart,
    FaEye,
    FaCommentDots
} from 'react-icons/fa';
import useActivityTracker from '../../hooks/useActivityTracker';
import './homepage.css';

const STRANDS_API_URL = process.env.REACT_APP_STRANDS_API_URL || 'http://localhost:5001';
// Búsqueda de noticias vive en nuestro backend ECS (antes en Vercel/serpapi-monitoreo).
const MEDIA_NEWS_SEARCH_URL = `${STRANDS_API_URL}/api/media-news/search`;

// Keywords por defecto genéricas (solo se usan si hay error de API)
const FALLBACK_KEYWORDS = [
    'industria', 'mercado', 'competencia', 'precios', 'tendencias',
    'regulación', 'noticias sector', 'análisis de mercado'
];

// Países soportados por el selector. El idioma (hl en SerpAPI) lo deriva el
// backend desde COUNTRY_LANG_MAP — aquí solo decidimos qué mostrar al usuario.
// Mantener sincronizado con backend/routes/media_news.py:COUNTRY_LANG_MAP.
// Card aislado de TikTok: maneja en estado local los fallos de carga de
// thumbnail y avatar (las URLs firmadas de TikTokCDN a veces caducan o las
// bloquean adblockers, y necesitamos fallback visual no roto).
// Convierte un timestamp de publicación a texto relativo ("Hoy", "Ayer",
// "Hace 3 días", "Hace 5 meses", "Hace 2 años"). Devuelve '' si la fecha
// no es válida. Recibe `t` para i18n; si no se pasa, fallback a español.
const formatPostAge = (dateString, t) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    if (!Number.isFinite(d.getTime())) return '';
    const now = Date.now();
    const diffMs = now - d.getTime();
    if (diffMs < 0) return ''; // post en el futuro = dato corrupto, no mostramos
    const diffDays = Math.floor(diffMs / 86400000);
    const tr = (key, params) => t ? t(`homepage.${key}`, params) : key;
    if (diffDays === 0) return tr('today');
    if (diffDays === 1) return tr('yesterday');
    // agoPrefix puede ser vacío (en inglés "3 days ago" no lleva prefijo).
    const join = (...parts) => parts.filter(Boolean).join(' ').trim();
    if (diffDays < 30) return join(tr('agoPrefix'), String(diffDays), tr('daysAgo'));
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths < 12) return join(tr('agoPrefix'), String(diffMonths), tr(diffMonths === 1 ? 'monthAgo' : 'monthsAgo'));
    const diffYears = Math.floor(diffDays / 365);
    return join(tr('agoPrefix'), String(diffYears), tr(diffYears === 1 ? 'yearAgo' : 'yearsAgo'));
};

const TikTokCard = ({ post, index, formatCompact, isSaved, onToggleSave, onDelete, onOpen, t }) => {
    const [thumbBroken, setThumbBroken] = useState(false);
    const [avatarBroken, setAvatarBroken] = useState(false);

    const authorLabel = post.author?.name || post.author?.username || 'TikTok';
    const handle = post.author?.username ? `@${post.author.username}` : null;
    const hasUsableThumb = post.thumbnail && !thumbBroken;
    const hasUsableAvatar = post.author?.avatar && !avatarBroken;
    const canSave = typeof onToggleSave === 'function' && post.url;
    const canDelete = typeof onDelete === 'function' && post.url;
    const ageText = formatPostAge(post.created_at, t);

    const handleCardClick = () => {
        if (!post.url) return;
        if (typeof onOpen === 'function') {
            onOpen(post);
        } else {
            window.open(post.url, '_blank');
        }
    };

    return (
        <div
            key={post.id || `tiktok-${index}`}
            className={`tiktok-card fade-in-up ${!post.url ? 'disabled' : ''}`}
            style={{ animationDelay: `${index * 0.04}s` }}
            onClick={handleCardClick}
        >
            <div className="tiktok-thumb">
                {hasUsableThumb ? (
                    <img
                        src={post.thumbnail}
                        alt={post.description?.slice(0, 50) || 'TikTok'}
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={() => setThumbBroken(true)}
                    />
                ) : (
                    <div className="tiktok-thumb-placeholder">
                        <FaVideo />
                    </div>
                )}
                {post.keyword && (
                    <div className="tiktok-keyword-badge">
                        {post.keyword.replace(/^#/, '')}
                    </div>
                )}
                {ageText && (
                    <div className="tiktok-age-badge" title={post.created_at}>
                        {ageText}
                    </div>
                )}
                {canDelete && (
                    <button
                        type="button"
                        className="tiktok-delete-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete(post);
                        }}
                        title="Ocultar este TikTok (no volverá a aparecer)"
                    >
                        <FaTrash />
                    </button>
                )}
                {canSave && (
                    <button
                        type="button"
                        className={`tiktok-save-btn ${isSaved ? 'saved' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onToggleSave(post);
                        }}
                        title={isSaved ? 'Quitar de destacados' : 'Destacar TikTok'}
                    >
                        {isSaved ? <FaStar /> : <FaRegStar />}
                    </button>
                )}
            </div>
            <div className="tiktok-content">
                <div className="tiktok-author">
                    {hasUsableAvatar ? (
                        <img
                            className="tiktok-avatar"
                            src={post.author.avatar}
                            alt={authorLabel}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            onError={() => setAvatarBroken(true)}
                        />
                    ) : (
                        <div className="tiktok-avatar tiktok-avatar-placeholder">
                            {authorLabel.charAt(0).toUpperCase()}
                        </div>
                    )}
                    <div className="tiktok-author-text">
                        <span className="tiktok-author-name">{authorLabel}</span>
                        {handle && (
                            <span className="tiktok-author-handle">{handle}</span>
                        )}
                    </div>
                </div>
                {post.description && (
                    <p className="tiktok-description">{post.description}</p>
                )}
                <div className="tiktok-metrics">
                    <span className="tiktok-metric">
                        <FaEye /> {formatCompact(post.metrics?.views)}
                    </span>
                    <span className="tiktok-metric">
                        <FaHeart /> {formatCompact(post.metrics?.likes)}
                    </span>
                    <span className="tiktok-metric">
                        <FaCommentDots /> {formatCompact(post.metrics?.comments)}
                    </span>
                </div>
            </div>
        </div>
    );
};

// Cap de comentarios a persistir (mantener sincronizado con backend
// COMMENTS_MAX_PER_POST).
const COMMENTS_HARD_CAP = 1000;

// Mapeo de sentiment → metadata visual (color, emoji, label en español).
const SENTIMENT_META = {
    pos: { color: '#10b981', emoji: '😊', label: 'positivo' },
    neu: { color: '#94a3b8', emoji: '😐', label: 'neutral' },
    neg: { color: '#ef4444', emoji: '😡', label: 'negativo' },
};

const SentimentPanel = ({ sentiment, t, onReanalyze, analyzing }) => {
    if (!sentiment?.aggregate) return null;
    const agg = sentiment.aggregate;
    const counts = agg.counts || { pos: 0, neu: 0, neg: 0 };
    const total = (counts.pos || 0) + (counts.neu || 0) + (counts.neg || 0);
    const pct = (n) => total > 0 ? Math.round((n / total) * 100) : 0;
    return (
        <div className="tt-sentiment-panel">
            <div className="tt-sentiment-header">
                <h4>{t('homepage.sentimentTitle')}</h4>
                <button
                    type="button"
                    className="tt-sentiment-refresh"
                    onClick={onReanalyze}
                    disabled={analyzing}
                    title={t('homepage.sentimentReanalyze')}
                >
                    <FaSync className={analyzing ? 'spinning' : ''} />
                    {' '}{t('homepage.sentimentReanalyze')}
                </button>
            </div>
            <div className="tt-sentiment-bar">
                {counts.pos > 0 && (
                    <div className="tt-sentiment-bar-segment pos" style={{ width: `${pct(counts.pos)}%` }} title={`${counts.pos} ${SENTIMENT_META.pos.label}`}>
                        {SENTIMENT_META.pos.emoji} {pct(counts.pos)}%
                    </div>
                )}
                {counts.neu > 0 && (
                    <div className="tt-sentiment-bar-segment neu" style={{ width: `${pct(counts.neu)}%` }} title={`${counts.neu} ${SENTIMENT_META.neu.label}`}>
                        {SENTIMENT_META.neu.emoji} {pct(counts.neu)}%
                    </div>
                )}
                {counts.neg > 0 && (
                    <div className="tt-sentiment-bar-segment neg" style={{ width: `${pct(counts.neg)}%` }} title={`${counts.neg} ${SENTIMENT_META.neg.label}`}>
                        {SENTIMENT_META.neg.emoji} {pct(counts.neg)}%
                    </div>
                )}
            </div>
            {agg.summary && (
                <p className="tt-sentiment-summary">{agg.summary}</p>
            )}
            {Array.isArray(agg.themes) && agg.themes.length > 0 && (
                <div className="tt-sentiment-themes">
                    <span className="tt-sentiment-themes-label">{t('homepage.sentimentThemes')}:</span>
                    {agg.themes.slice(0, 5).map((th, i) => (
                        <span key={i} className="tt-sentiment-theme-chip">
                            {th.name} <em>({th.count})</em>
                        </span>
                    ))}
                </div>
            )}
            {(agg.spotlight_positive?.text || agg.spotlight_negative?.text) && (
                <div className="tt-sentiment-spotlights">
                    {agg.spotlight_positive?.text && (
                        <div className="tt-sentiment-spotlight pos">
                            <span className="tt-sentiment-spotlight-label">💡 {t('homepage.sentimentTopPositive')}</span>
                            <p>"{agg.spotlight_positive.text}"</p>
                        </div>
                    )}
                    {agg.spotlight_negative?.text && (
                        <div className="tt-sentiment-spotlight neg">
                            <span className="tt-sentiment-spotlight-label">⚠️ {t('homepage.sentimentTopNegative')}</span>
                            <p>"{agg.spotlight_negative.text}"</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const CommentRow = ({ comment, formatCompact, children, isReply, t, sentimentByComment }) => {
    const [avatarBroken, setAvatarBroken] = useState(false);
    const author = comment.commenter || {};
    const label = author.name || author.username || 'Anon';
    const hasAvatar = author.avatar && !avatarBroken;
    const hasRendered = Array.isArray(children) && children.length > 0;
    // Si hay replies pero no las trajimos anidadas, ofrecemos abrir el
    // thread directo en TikTok usando comment_url (incluye ?comment_id=).
    const showThreadLink = !hasRendered && comment.replies > 0 && comment.comment_url;
    // Badge de sentiment si hay análisis disponible para este comment_id.
    const senti = sentimentByComment && comment.id ? sentimentByComment[comment.id] : null;
    const sentiMeta = senti && SENTIMENT_META[senti.sentiment];
    return (
        <div className={`tt-comment-row ${isReply ? 'tt-comment-reply' : ''}`}>
            {hasAvatar ? (
                <img
                    className="tt-comment-avatar"
                    src={author.avatar}
                    alt={label}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={() => setAvatarBroken(true)}
                />
            ) : (
                <div className="tt-comment-avatar tt-comment-avatar-placeholder">
                    {label.charAt(0).toUpperCase()}
                </div>
            )}
            <div className="tt-comment-body">
                <div className="tt-comment-header">
                    {sentiMeta && (
                        <span
                            className={`tt-sentiment-dot tt-sentiment-${senti.sentiment}`}
                            title={`${sentiMeta.label}${senti.theme ? ' · ' + senti.theme : ''}`}
                            aria-label={sentiMeta.label}
                        />
                    )}
                    <strong>{label}</strong>
                    {author.username && author.username !== label && (
                        <span className="tt-comment-handle">@{author.username}</span>
                    )}
                </div>
                {comment.text && <p className="tt-comment-text">{comment.text}</p>}
                <div className="tt-comment-footer">
                    <span><FaHeart /> {formatCompact(comment.likes || 0)}</span>
                    {!hasRendered && comment.replies > 0 && !showThreadLink && (
                        <span><FaCommentDots /> {formatCompact(comment.replies)}</span>
                    )}
                    {showThreadLink && (
                        <a
                            className="tt-comment-thread-link"
                            href={comment.comment_url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <FaCommentDots />
                            {' '}
                            {t
                                ? t('homepage.commentsViewThread', { count: formatCompact(comment.replies) })
                                : `Ver ${formatCompact(comment.replies)} respuestas`}
                            {' '}
                            <FaExternalLinkAlt />
                        </a>
                    )}
                </div>
                {hasRendered && (
                    <div className="tt-comment-replies">
                        {children}
                    </div>
                )}
            </div>
        </div>
    );
};

// Agrupa una lista plana de comentarios en árbol parent → children usando
// `parent_id`. Comentarios huérfanos (parent_id sin match) caen a top-level
// para no perderlos.
const buildCommentTree = (comments) => {
    const byId = new Map();
    comments.forEach(c => byId.set(c.id, { ...c, children: [] }));
    const roots = [];
    comments.forEach(c => {
        const node = byId.get(c.id);
        const parent = c.parent_id && byId.get(c.parent_id);
        if (parent) {
            parent.children.push(node);
        } else {
            roots.push(node);
        }
    });
    // Sort top-level y replies por likes desc.
    const sortByLikes = (a, b) => (b.likes || 0) - (a.likes || 0);
    roots.sort(sortByLikes);
    roots.forEach(r => r.children.sort(sortByLikes));
    return roots;
};

const TikTokCommentsModal = ({
    post, onClose, t, formatCompact, formatDate, apiUrl,
    isSaved, onSave, sentimentEnabled, username, trackAction,
}) => {
    const [status, setStatus] = useState('idle');
    const [comments, setComments] = useState([]);
    const [fetchedAt, setFetchedAt] = useState(null);
    const [errorMsg, setErrorMsg] = useState(null);
    const [sentiment, setSentiment] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [sentimentError, setSentimentError] = useState(null);

    const postId = post?.id || '';
    const postUrl = post?.url || '';
    const commentCount = post?.metrics?.comments || 0;
    const exceedsCap = commentCount > COMMENTS_HARD_CAP;

    // Árbol de comentarios para render anidado.
    const commentTree = useMemo(() => buildCommentTree(comments), [comments]);

    // Cargar estado actual al abrir
    useEffect(() => {
        if (!postId) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(
                    `${apiUrl}/api/media-social/comments?post_id=${encodeURIComponent(postId)}&platform=tiktok`,
                );
                const data = await res.json();
                if (cancelled) return;
                if (data.success) {
                    setStatus(data.status || 'idle');
                    setComments(data.comments || []);
                    setFetchedAt(data.fetched_at);
                    setSentiment(data.sentiment || null);
                    if (data.error) setErrorMsg(data.error);
                }
            } catch (e) {
                console.warn('[Comments] load error', e);
            }
        })();
        return () => { cancelled = true; };
    }, [postId, apiUrl]);

    // Polling mientras está pending
    useEffect(() => {
        if (status !== 'pending' || !postId) return;
        let cancelled = false;
        const tick = async () => {
            try {
                const res = await fetch(
                    `${apiUrl}/api/media-social/comments?post_id=${encodeURIComponent(postId)}&platform=tiktok`,
                );
                const data = await res.json();
                if (cancelled) return;
                if (data.success) {
                    setStatus(data.status);
                    setComments(data.comments || []);
                    setFetchedAt(data.fetched_at);
                    setSentiment(data.sentiment || null);
                    if (data.error) setErrorMsg(data.error);
                }
            } catch (e) {
                // siguiente tick reintenta
            }
        };
        const id = setInterval(tick, 30000);
        return () => { cancelled = true; clearInterval(id); };
    }, [status, postId, apiUrl]);

    // Disparar análisis de sentimientos. Sincrónico — Haiku responde en pocos segundos.
    const analyzeComments = useCallback(async () => {
        if (!postId || analyzing) return;
        if (typeof trackAction === 'function') {
            trackAction('analyze_sentiment', {
                post_id: postId,
                comments_count: comments.length,
                is_reanalysis: Boolean(sentiment),
            });
        }
        setAnalyzing(true);
        setSentimentError(null);
        try {
            const res = await fetch(`${apiUrl}/api/media-social/comments/analyze`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ post_id: postId, platform: 'tiktok', username }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.success) {
                setSentimentError(data.error || t('homepage.sentimentErrorGeneric'));
            } else {
                setSentiment(data.sentiment);
            }
        } catch (e) {
            setSentimentError(t('homepage.sentimentErrorNetwork'));
        } finally {
            setAnalyzing(false);
        }
    }, [postId, apiUrl, t, analyzing, username, trackAction, comments.length, sentiment]);

    const triggerLoad = useCallback(async () => {
        if (!postId || !postUrl) return;
        if (typeof trackAction === 'function') {
            trackAction('load_comments', {
                post_id: postId,
                post_url: postUrl,
                comment_count_hint: post?.metrics?.comments,
                is_re_load: Boolean(comments && comments.length > 0),
            });
        }
        // Al cargar comentarios, auto-destacar el TikTok (idempotente).
        if (!isSaved && typeof onSave === 'function') {
            onSave(post);
        }
        setStatus('pending');
        setErrorMsg(null);
        // Invalidamos el sentiment local — el backend lo hace en _save_comments_ready,
        // pero el frontend reflejaba el viejo hasta el próximo GET.
        setSentiment(null);
        setSentimentError(null);
        try {
            const res = await fetch(`${apiUrl}/api/media-social/comments/refresh`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ post_id: postId, post_url: postUrl, platform: 'tiktok', username }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.success) {
                setStatus('failed');
                setErrorMsg(data.error || t('homepage.commentsErrorGeneric'));
            }
        } catch (e) {
            setStatus('failed');
            setErrorMsg(t('homepage.commentsErrorNetwork'));
        }
    }, [postId, postUrl, apiUrl, t, isSaved, onSave, post, username, trackAction, comments]);

    if (!post) return null;

    const authorLabel = post.author?.name || post.author?.username || 'TikTok';

    return (
        <div className="tt-modal-backdrop" onClick={onClose}>
            <div className="tt-modal" onClick={(e) => e.stopPropagation()}>
                <button
                    type="button"
                    className="tt-modal-close"
                    onClick={onClose}
                    title={t('homepage.commentsClose')}
                >
                    <FaTimes />
                </button>

                <div className="tt-modal-header">
                    {post.thumbnail && (
                        <img
                            className="tt-modal-thumb"
                            src={post.thumbnail}
                            alt={authorLabel}
                            referrerPolicy="no-referrer"
                        />
                    )}
                    <div className="tt-modal-info">
                        <div className="tt-modal-author">
                            <strong>{authorLabel}</strong>
                            {post.author?.username && (
                                <span className="tt-modal-handle">@{post.author.username}</span>
                            )}
                        </div>
                        {post.description && (
                            <p className="tt-modal-desc">{post.description}</p>
                        )}
                        <div className="tt-modal-metrics">
                            <span><FaEye /> {formatCompact(post.metrics?.views)}</span>
                            <span><FaHeart /> {formatCompact(post.metrics?.likes)}</span>
                            <span><FaCommentDots /> {formatCompact(commentCount)}</span>
                        </div>
                        <a
                            className="tt-modal-link"
                            href={post.url}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {t('homepage.commentsViewOnTiktok')} <FaExternalLinkAlt />
                        </a>
                    </div>
                </div>

                <div className="tt-modal-body">
                    <h3 className="tt-modal-section-title">
                        <FaCommentDots /> {t('homepage.commentsTitle')}
                    </h3>

                    {/* Idle: confirmación previa */}
                    {status === 'idle' && (
                        <div className="tt-comments-confirm">
                            <FaInfoCircle />
                            <div className="tt-comments-confirm-text">
                                <p>
                                    {t('homepage.commentsConfirmCount', {
                                        count: formatCompact(commentCount),
                                    })}
                                </p>
                                <p className="tt-comments-cap-note">
                                    {exceedsCap
                                        ? t('homepage.commentsCapExceeded', { cap: COMMENTS_HARD_CAP })
                                        : t('homepage.commentsCap', { cap: COMMENTS_HARD_CAP })}
                                </p>
                            </div>
                            <button
                                type="button"
                                className="section-refresh-btn"
                                onClick={triggerLoad}
                                disabled={!postUrl}
                            >
                                <FaSync /> {t('homepage.commentsLoadButton')}
                            </button>
                        </div>
                    )}

                    {/* Pending */}
                    {status === 'pending' && (
                        <div className="tt-comments-pending">
                            <FaSync className="spinning" />
                            <p>{t('homepage.commentsPending')}</p>
                        </div>
                    )}

                    {/* Failed */}
                    {status === 'failed' && (
                        <div className="tt-comments-error">
                            <FaInfoCircle />
                            <span>{errorMsg || t('homepage.commentsErrorGeneric')}</span>
                            <button
                                type="button"
                                className="section-refresh-btn"
                                onClick={triggerLoad}
                            >
                                <FaSync /> {t('homepage.commentsRetry')}
                            </button>
                        </div>
                    )}

                    {/* Ready */}
                    {status === 'ready' && (
                        <>
                            <div className="tt-comments-meta">
                                <span>
                                    {t('homepage.commentsLoadedMeta', {
                                        count: comments.length,
                                        when: fetchedAt ? formatDate(fetchedAt) : '',
                                    })}
                                </span>
                                <div className="tt-comments-meta-actions">
                                    {sentimentEnabled && comments.length > 0 && !sentiment && (
                                        <button
                                            type="button"
                                            className="tt-sentiment-cta"
                                            onClick={analyzeComments}
                                            disabled={analyzing}
                                            title={t('homepage.sentimentCta')}
                                        >
                                            {analyzing ? <FaSync className="spinning" /> : '🧠'}
                                            {' '}{analyzing
                                                ? t('homepage.sentimentAnalyzing')
                                                : t('homepage.sentimentCta')}
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="tt-comments-refresh-link"
                                        onClick={triggerLoad}
                                        title={t('homepage.commentsLoadButton')}
                                    >
                                        <FaSync /> {t('homepage.commentsLoadButton')}
                                    </button>
                                </div>
                            </div>

                            {sentimentEnabled && sentimentError && (
                                <div className="tt-comments-error">
                                    <FaInfoCircle />
                                    <span>{sentimentError}</span>
                                </div>
                            )}

                            {sentimentEnabled && sentiment?.aggregate && (
                                <SentimentPanel
                                    sentiment={sentiment}
                                    t={t}
                                    onReanalyze={analyzeComments}
                                    analyzing={analyzing}
                                />
                            )}

                            {comments.length === 0 ? (
                                <div className="tt-comments-empty">
                                    {t('homepage.commentsEmpty')}
                                </div>
                            ) : (
                                <div className="tt-comments-list">
                                    {commentTree.map((c) => (
                                        <CommentRow
                                            key={c.id || `${c.commenter?.username}-${c.created_at}`}
                                            comment={c}
                                            formatCompact={formatCompact}
                                            t={t}
                                            sentimentByComment={sentimentEnabled ? sentiment?.per_comment : null}
                                        >
                                            {c.children.map((child) => (
                                                <CommentRow
                                                    key={child.id || `${child.commenter?.username}-${child.created_at}`}
                                                    comment={child}
                                                    formatCompact={formatCompact}
                                                    t={t}
                                                    isReply
                                                    sentimentByComment={sentimentEnabled ? sentiment?.per_comment : null}
                                                />
                                            ))}
                                        </CommentRow>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const COUNTRIES = [
    { code: 'mx', label: 'México' },
    { code: 'co', label: 'Colombia' },
    { code: 'cl', label: 'Chile' },
    { code: 'ar', label: 'Argentina' },
    { code: 'pe', label: 'Perú' },
    { code: 'br', label: 'Brasil' },
    { code: 'es', label: 'España' },
    { code: 'us', label: 'Estados Unidos' },
];
const DEFAULT_COUNTRY = 'mx';

const HomePage = ({ userFeatures = {} }) => {
    // Sub-módulos por usuario. Defaults restrictivos en el frontend:
    //   - news on por default (todos los usuarios habilitados)
    //   - tiktok off por default (solo se activa para usuarios autorizados)
    // Si userFeatures no llega (carga inicial), usamos los defaults para
    // no parpadear vacío.
    const newsEnabled = userFeatures.mediaMonitoringNews !== false;
    // TikTok se movió a "Por Cuenta" (la keyword se trata como cuenta). Esta vista
    // ("Noticias") ya solo muestra noticias. Forzamos off para ocultar pestaña,
    // carga de datos y redirección de TikTok sin tocar el flujo de noticias.
    const tiktokEnabled = false;
    const sentimentEnabled = userFeatures.commentSentimentAnalysis === true;
    // Helper para escoger el primer tab disponible cuando el activo se
    // deshabilita o cuando llegan las features asincronamente.
    const pickInitialTab = useCallback(() => {
        if (newsEnabled) return 'feed';
        if (tiktokEnabled) return 'tiktok';
        return 'saved';
    }, [newsEnabled, tiktokEnabled]);
    const { user } = useAuthenticator((context) => [context.user]);
    const { t } = useTranslation();
    const { trackAction } = useActivityTracker(user?.username, 'media_monitoring');

    // Estados para noticias
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Mensaje informativo (no-error): ej. "Sin noticias nuevas" cuando un
    // refresh manual no trajo resultados pero ya hay noticias visibles.
    const [infoMessage, setInfoMessage] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    // Filtro de noticias por keyword: '' = todas. Cuando el usuario elige una
    // keyword en el select, solo mostramos artículos cuyo título/descripción
    // la contienen.
    const [filterKeyword, setFilterKeyword] = useState('');
    // Filtro por fecha exacta (YYYY-MM-DD). '' = todas las fechas.
    const [filterDate, setFilterDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const NEWS_PAGE_SIZE = 20;

    // Pestañas: 'feed' (búsqueda actual del mes) | 'tiktok' | 'saved' (destacadas).
    // Inicializamos en el primer sub-módulo habilitado; 'saved' como último
    // recurso (siempre está disponible porque solo lista local data).
    const [activeTab, setActiveTab] = useState(pickInitialTab);
    // Spinner inline mientras buscamos noticias para una fecha que el usuario
    // filtró pero que no tiene noticias en el pool actual.
    const [dateSearching, setDateSearching] = useState(false);
    // Set de fechas que ya intentamos buscar. Usamos useRef en vez de
    // useState porque setState es async y un useEffect podía disparar varias
    // veces la misma búsqueda antes de que el set se actualizara (race
    // condition que llegó a inflar el pool a cientos de noticias falsas).
    const searchedDatesRef = useRef(new Set());
    // Noticias destacadas del usuario (persisten entre sesiones).
    const [savedArticles, setSavedArticles] = useState([]);
    // Set de article URLs guardadas, para pintar la estrella rápido.
    const savedUrls = useMemo(
        () => new Set(savedArticles.map(a => a?.url).filter(Boolean)),
        [savedArticles]
    );

    // Estados para keywords
    const [keywords, setKeywords] = useState([]);
    const [newKeyword, setNewKeyword] = useState('');
    const [showKeywordsPanel, setShowKeywordsPanel] = useState(false);
    const [keywordsLoading, setKeywordsLoading] = useState(false);
    const [keywordsSaving, setKeywordsSaving] = useState(false);
    const [keywordsLastUpdated, setKeywordsLastUpdated] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [hasCustomKeywords, setHasCustomKeywords] = useState(false);
    const [keywordsLimitError, setKeywordsLimitError] = useState('');

    // Estados para presets
    const [presets, setPresets] = useState({});
    const [showPresets, setShowPresets] = useState(false);
    const [selectedPreset, setSelectedPreset] = useState(null);

    // Post de TikTok abierto en el modal de comentarios (null = cerrado).
    const [activeCommentsPost, setActiveCommentsPost] = useState(null);

    // Wrapper que abre el modal y trackea la acción. Memoizado para no
    // re-renderizar TikTokCard innecesariamente.
    const openCommentsModal = useCallback((post) => {
        trackAction('open_tiktok_modal', {
            post_id: post?.id,
            post_url: post?.url,
            author: post?.author?.username,
        });
        setActiveCommentsPost(post);
    }, [trackAction]);

    // País del que se buscan las noticias (parametro `gl` de SerpAPI). El
    // backend lo persiste por usuario en user_database_config.media_news_country.
    // Inicializamos en default y lo sobreescribimos cuando carga la preferencia.
    const [country, setCountry] = useState(DEFAULT_COUNTRY);
    const [countryLoaded, setCountryLoaded] = useState(false);
    const [savingCountry, setSavingCountry] = useState(false);

    // ----- TikTok -----
    // Lista normalizada (shape lo define el backend en _normalize_tiktok_post).
    const [tiktokPosts, setTiktokPosts] = useState([]);
    // Status del snapshot: idle (nunca disparado) | pending | ready | failed.
    const [tiktokStatus, setTiktokStatus] = useState('idle');
    const [tiktokFetchedAt, setTiktokFetchedAt] = useState(null);
    const [tiktokTriggeredAt, setTiktokTriggeredAt] = useState(null);
    const [tiktokError, setTiktokError] = useState(null);
    const [tiktokRefreshing, setTiktokRefreshing] = useState(false);
    // Criterio de ordenamiento del grid de TikToks. Default 'views' porque
    // el caso de uso típico es ver los más virales primero.
    const [tiktokSortBy, setTiktokSortBy] = useState('views');
    // Filtro por keyword del feed TikTok ('' = todas). Se aplica ANTES del sort.
    const [tiktokKeywordFilter, setTiktokKeywordFilter] = useState('');
    // TikToks destacados del usuario (persisten entre sesiones).
    const [savedTiktoks, setSavedTiktoks] = useState([]);
    // Set de URLs guardadas para pintar la estrella en O(1).
    const savedTiktokUrls = useMemo(
        () => new Set(savedTiktoks.map(p => p?.url).filter(Boolean)),
        [savedTiktoks]
    );

    // Conteo de posts por keyword, para poblar el filtro con su cardinalidad.
    const tiktokCountByKeyword = useMemo(() => {
        const counts = {};
        for (const p of tiktokPosts) {
            const k = p?.keyword || '';
            if (!k) continue;
            counts[k] = (counts[k] || 0) + 1;
        }
        return counts;
    }, [tiktokPosts]);

    // Posts filtrados por keyword y luego ordenados según el sort seleccionado.
    // El filtro se aplica primero, así el orden elegido manda sobre el subconjunto.
    // Empate → mantenemos el orden original que entrega el backend.
    const sortedTiktokPosts = useMemo(() => {
        let arr = [...tiktokPosts];
        if (tiktokKeywordFilter) {
            arr = arr.filter(p => (p?.keyword || '') === tiktokKeywordFilter);
        }
        const num = (v) => (typeof v === 'number' ? v : 0);
        const ts = (v) => {
            if (!v) return 0;
            const t = new Date(v).getTime();
            return Number.isFinite(t) ? t : 0;
        };
        switch (tiktokSortBy) {
            case 'recent':
                return arr.sort((a, b) => ts(b.created_at) - ts(a.created_at));
            case 'oldest':
                // Posts sin fecha al final para no contaminar el top.
                return arr.sort((a, b) => {
                    const ta = ts(a.created_at);
                    const tb = ts(b.created_at);
                    if (ta === 0 && tb !== 0) return 1;
                    if (tb === 0 && ta !== 0) return -1;
                    return ta - tb;
                });
            case 'likes':
                return arr.sort((a, b) => num(b.metrics?.likes) - num(a.metrics?.likes));
            case 'comments':
                return arr.sort((a, b) => num(b.metrics?.comments) - num(a.metrics?.comments));
            case 'views':
            default:
                return arr.sort((a, b) => num(b.metrics?.views) - num(a.metrics?.views));
        }
    }, [tiktokPosts, tiktokSortBy, tiktokKeywordFilter]);
    // Umbral para auto-refresh: si el snapshot tiene >X días, disparamos uno
    // nuevo en background al abrir la tab. Manual override siempre disponible.
    const TIKTOK_STALE_DAYS = 7;

    // Ref para input
    const keywordInputRef = useRef(null);

    // Obtener keywords por defecto (FALLBACK)
    const getDefaultKeywords = useCallback(() => {
        return FALLBACK_KEYWORDS;
    }, []);

    // Cargar keywords del usuario desde /api/media-keywords
    // Este endpoint devuelve las keywords del usuario desde user_database_config
    const loadKeywords = useCallback(async () => {
        if (!user?.username) return;

        try {
            setKeywordsLoading(true);
            console.log('[KEYWORDS] Cargando keywords para usuario:', user.username);
            const response = await fetch(
                `${STRANDS_API_URL}/api/media-keywords?username=${encodeURIComponent(user.username)}`
            );
            const data = await response.json();
            console.log('[KEYWORDS] Respuesta de /api/media-keywords:', {
                success: data.success,
                keywordsCount: data.keywords?.length || 0,
                firstKeyword: data.keywords?.[0] || 'N/A',
                lastUpdated: data.last_updated || 'N/A'
            });

            if (data.success && data.keywords && data.keywords.length > 0) {
                // Usar las keywords que devuelve el API (del usuario o defaults del backend)
                console.log('[KEYWORDS] Usando keywords del API:', data.keywords.length);
                setKeywords(data.keywords);
                setKeywordsLastUpdated(data.last_updated);
                // Si last_updated tiene valor, el usuario ha personalizado sus keywords
                setHasCustomKeywords(!!data.last_updated);
            } else {
                // Error o API vacío - usar fallback del frontend
                console.log('[KEYWORDS] API vacío, usando FALLBACK_KEYWORDS');
                setKeywords(FALLBACK_KEYWORDS);
                setHasCustomKeywords(false);
            }
        } catch (err) {
            console.error('[KEYWORDS] Error loading:', err);
            setKeywords(FALLBACK_KEYWORDS);
            setHasCustomKeywords(false);
        } finally {
            setKeywordsLoading(false);
        }
    }, [user?.username]);

    // Cargar país preferido del usuario. Si el endpoint falla o el usuario no
    // tiene preferencia, dejamos el default ('mx').
    const loadCountry = useCallback(async () => {
        if (!user?.username) return;
        try {
            const res = await fetch(
                `${STRANDS_API_URL}/api/media-news/country?username=${encodeURIComponent(user.username)}`
            );
            if (!res.ok) return;
            const data = await res.json();
            if (data.success && data.country) {
                setCountry(data.country);
            }
        } catch (e) {
            console.warn('[HomePage] Error cargando país:', e);
        } finally {
            setCountryLoaded(true);
        }
    }, [user?.username]);

    // ----- TikTok -----
    // Carga el último snapshot guardado. No dispara nada nuevo. Devuelve
    // el status para que el caller decida si mostrar UI de "pendiente",
    // "vacío", "fresco", etc.
    const loadTiktok = useCallback(async () => {
        if (!user?.username) return null;
        try {
            const res = await fetch(
                `${STRANDS_API_URL}/api/media-social?username=${encodeURIComponent(user.username)}&platform=tiktok`
            );
            if (!res.ok) return null;
            const data = await res.json();
            if (data.success) {
                setTiktokPosts(Array.isArray(data.posts) ? data.posts : []);
                setTiktokStatus(data.status || 'idle');
                setTiktokFetchedAt(data.fetched_at);
                setTiktokTriggeredAt(data.triggered_at);
                setTiktokError(data.error || null);
                return data;
            }
        } catch (e) {
            console.warn('[HomePage] Error cargando TikTok:', e);
        }
        return null;
    }, [user?.username]);

    // Carga los TikToks destacados del usuario.
    const loadSavedTiktoks = useCallback(async () => {
        if (!user?.username) return;
        try {
            const res = await fetch(
                `${STRANDS_API_URL}/api/saved-social-posts?username=${encodeURIComponent(user.username)}&platform=tiktok`
            );
            if (!res.ok) return;
            const data = await res.json();
            if (data.success && Array.isArray(data.posts)) {
                setSavedTiktoks(data.posts);
            }
        } catch (e) {
            console.warn('[HomePage] Error cargando TikToks destacados:', e);
        }
    }, [user?.username]);

    // Toggle destacar/quitar de destacados un TikTok.
    const toggleSaveTiktok = useCallback(async (post) => {
        if (!user?.username || !post?.url) return;
        const isSaved = savedTiktokUrls.has(post.url);

        trackAction('save_tiktok_toggle', {
            action_sub: isSaved ? 'unsave' : 'save',
            post_id: post.id,
            post_url: post.url,
        });

        // Optimistic update.
        if (isSaved) {
            setSavedTiktoks(prev => prev.filter(p => p.url !== post.url));
        } else {
            setSavedTiktoks(prev => [post, ...prev]);
        }

        try {
            if (isSaved) {
                await fetch(
                    `${STRANDS_API_URL}/api/saved-social-posts?` +
                    `username=${encodeURIComponent(user.username)}&` +
                    `platform=tiktok&` +
                    `post_url=${encodeURIComponent(post.url)}`,
                    { method: 'DELETE' }
                );
            } else {
                await fetch(`${STRANDS_API_URL}/api/saved-social-posts`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        username: user.username,
                        platform: 'tiktok',
                        post,
                    }),
                });
            }
        } catch (e) {
            console.warn('[HomePage] Error guardando/quitando TikTok destacado:', e);
            // Revertir el optimistic update.
            loadSavedTiktoks();
        }
    }, [user?.username, savedTiktokUrls, loadSavedTiktoks, trackAction]);

    // Oculta un TikTok (blocklist persistente). El backend lo filtra al leer,
    // así no reaparece ni al recargar ni al disparar un snapshot nuevo.
    const hideTiktok = useCallback(async (post) => {
        if (!user?.username || !post?.url) return;
        const ok = window.confirm(
            '¿Ocultar este TikTok? No volverá a aparecer en el monitoreo, ni siquiera al actualizar.'
        );
        if (!ok) return;

        trackAction('hide_tiktok', { post_id: post.id, post_url: post.url });

        // Optimistic: quitar del feed de inmediato.
        const prevPosts = tiktokPosts;
        setTiktokPosts(prev => prev.filter(p => p.url !== post.url));

        try {
            const res = await fetch(`${STRANDS_API_URL}/api/hidden-social-posts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: user.username,
                    platform: 'tiktok',
                    post,
                }),
            });
            if (!res.ok) throw new Error(`hide failed: ${res.status}`);
        } catch (e) {
            console.warn('[HomePage] Error ocultando TikTok:', e);
            // Revertir el optimistic update.
            setTiktokPosts(prevPosts);
        }
    }, [user?.username, tiktokPosts, trackAction]);

    // Dispara un snapshot nuevo en el backend. Respuesta inmediata (status=
    // pending), el polling lo hace el efecto separado abajo.
    const refreshTiktok = useCallback(async () => {
        if (!user?.username || tiktokRefreshing) return;
        setTiktokRefreshing(true);
        setTiktokError(null);
        trackAction('refresh_tiktok', {
            keywords_count: keywords.length,
            previous_status: tiktokStatus,
        });
        try {
            const res = await fetch(
                `${STRANDS_API_URL}/api/media-social/refresh?username=${encodeURIComponent(user.username)}&platform=tiktok`,
                { method: 'POST' }
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.success) {
                setTiktokError(data.error || 'No se pudo iniciar la búsqueda en TikTok');
                return;
            }
            setTiktokStatus('pending');
            setTiktokTriggeredAt(new Date().toISOString());
        } catch (e) {
            console.warn('[HomePage] Error iniciando refresh TikTok:', e);
            setTiktokError('Error de red al iniciar la búsqueda en TikTok');
        } finally {
            setTiktokRefreshing(false);
        }
    }, [user?.username, tiktokRefreshing, trackAction, keywords.length, tiktokStatus]);

    // Cargar presets
    const loadPresets = useCallback(async () => {
        try {
            const response = await fetch(`${STRANDS_API_URL}/api/media-keywords/presets`);
            const data = await response.json();

            if (data.success) {
                setPresets(data.presets || {});
            }
        } catch (err) {
            console.error('[PRESETS] Error loading:', err);
        }
    }, []);

    // Guardar keywords
    const saveKeywords = async () => {
        if (!user?.username) return;

        try {
            setKeywordsSaving(true);
            const response = await fetch(
                `${STRANDS_API_URL}/api/media-keywords?username=${encodeURIComponent(user.username)}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ keywords })
                }
            );
            const data = await response.json();

            if (data.success) {
                setKeywordsLastUpdated(data.last_updated);
                setHasChanges(false);
                setHasCustomKeywords(true);
                trackAction('save_keywords', {
                    keywords_count: keywords.length,
                    keywords_preview: keywords.slice(0, 10),
                });
            }
        } catch (err) {
            console.error('[KEYWORDS] Error saving:', err);
        } finally {
            setKeywordsSaving(false);
        }
    };

    // Guarda la preferencia de país. El backend invalida el cache de noticias
    // de hoy, así que limpiamos el feed local y disparamos un refetch para
    // que el usuario vea de inmediato las noticias del nuevo país.
    const handleCountryChange = async (newCountry) => {
        if (!user?.username || !newCountry || newCountry === country) return;
        const previous = country;
        trackAction('change_country', {
            from: previous,
            to: newCountry,
        });
        // Optimistic: actualizar UI antes del round-trip.
        setCountry(newCountry);
        setSavingCountry(true);
        try {
            const res = await fetch(
                `${STRANDS_API_URL}/api/media-news/country?username=${encodeURIComponent(user.username)}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ country: newCountry }),
                }
            );
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.success) {
                // Revertir si el backend rechazó
                setCountry(previous);
                setError(data.error || 'No se pudo cambiar el país');
                return;
            }
            // Limpiar feed actual y disparar búsqueda fresca con el nuevo
            // país. searchedDatesRef se resetea porque las búsquedas previas
            // por fecha eran del país viejo. Pasamos `newCountry` explícito
            // porque el setCountry de arriba todavía no se refleja en el
            // closure de fetchRetailNews.
            setNews([]);
            setLastUpdate(null);
            setError(null);
            setInfoMessage(null);
            searchedDatesRef.current = new Set();
            // Fire-and-forget: el setLoading lo maneja fetchRetailNews.
            fetchRetailNews(newCountry);
        } catch (e) {
            console.warn('[HomePage] Error guardando país:', e);
            setCountry(previous);
            setError('Error de red al cambiar el país');
        } finally {
            setSavingCountry(false);
        }
    };

    // Restaurar keywords por defecto de la organización
    const resetToDefault = () => {
        const defaultKws = getDefaultKeywords();
        if (window.confirm(t('homepage.confirmRestoreDefaults') || '¿Restaurar las keywords por defecto de la organización?')) {
            setKeywords(defaultKws);
            setHasChanges(true);
        }
    };

    // Agregar keyword
    const addKeyword = (keyword) => {
        const trimmed = keyword.trim();

        // Verificar límite de 20 keywords
        if (keywords.length >= 20) {
            setKeywordsLimitError(t('homepage.keywordsLimitError'));
            setNewKeyword('');
            return;
        }

        if (trimmed && !keywords.includes(trimmed)) {
            setKeywords(prev => [...prev, trimmed]);
            setHasChanges(true);
            setKeywordsLimitError(''); // Limpiar error si existe
        }
        setNewKeyword('');
    };

    // Eliminar keyword
    const removeKeyword = (keyword) => {
        setKeywords(prev => prev.filter(k => k !== keyword));
        setHasChanges(true);
        // Limpiar error de límite si existe (ya hay espacio para más keywords)
        if (keywordsLimitError) {
            setKeywordsLimitError('');
        }
    };

    // Aplicar preset
    const applyPreset = (presetKey) => {
        const preset = presets[presetKey];
        if (preset) {
            // Combinar keywords existentes con las del preset (sin duplicados)
            const combined = [...new Set([...keywords, ...preset.keywords])];
            setKeywords(combined);
            setHasChanges(true);
            setSelectedPreset(presetKey);
            setShowPresets(false);
        }
    };

    // Limpiar todas las keywords
    const clearAllKeywords = () => {
        if (window.confirm('¿Estás seguro de eliminar todas las keywords?')) {
            setKeywords([]);
            setHasChanges(true);
        }
    };

    // Helper: convierte artículos crudos al shape que renderiza el grid
    // (imagen fallback, url sanitizada).
    const decorateArticles = (articles) => articles.map((article, index) => ({
        ...article,
        image: article.image || `https://picsum.photos/350/200?random=${Date.now() + index}`,
        url: article.url && article.url !== '#' && article.url.startsWith('http')
            ? article.url
            : null
    }));

    // Carga las noticias destacadas del usuario desde la BD.
    const loadSavedNews = useCallback(async () => {
        if (!user?.username) return;
        try {
            const res = await fetch(
                `${STRANDS_API_URL}/api/saved-news?username=${encodeURIComponent(user.username)}`
            );
            if (!res.ok) return;
            const data = await res.json();
            if (data.success && Array.isArray(data.articles)) {
                setSavedArticles(decorateArticles(data.articles));
            }
        } catch (e) {
            console.warn('[HomePage] No se pudieron cargar destacadas:', e);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Toggle de destacar/quitar destacada para un artículo dado.
    const toggleSaveArticle = async (article) => {
        if (!user?.username || !article?.url) return;
        const isSaved = savedUrls.has(article.url);

        trackAction('save_news_toggle', {
            action_sub: isSaved ? 'unsave' : 'save',
            article_url: article.url,
            article_title: (article.title || '').slice(0, 200),
        });

        // Optimistic update para sentir la UI snappy.
        if (isSaved) {
            setSavedArticles(prev => prev.filter(a => a.url !== article.url));
        } else {
            setSavedArticles(prev => [article, ...prev]);
        }

        try {
            if (isSaved) {
                await fetch(
                    `${STRANDS_API_URL}/api/saved-news?` +
                    `username=${encodeURIComponent(user.username)}&` +
                    `article_url=${encodeURIComponent(article.url)}`,
                    { method: 'DELETE' }
                );
            } else {
                await fetch(`${STRANDS_API_URL}/api/saved-news`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ username: user.username, article })
                });
            }
        } catch (e) {
            console.warn('[HomePage] Error guardando/quitando destacada:', e);
            // Revertir el optimistic update si falla.
            loadSavedNews();
        }
    };

    // Lee el cache diario del backend. Retorna true si pudo poblar las
    // noticias desde el cache, false si no hay cache fresco (el caller
    // entonces dispara una búsqueda nueva).
    const loadCachedNews = useCallback(async () => {
        if (!user?.username) return false;
        try {
            const res = await fetch(
                `${STRANDS_API_URL}/api/media-news?username=${encodeURIComponent(user.username)}`
            );
            if (!res.ok) return false;
            const data = await res.json();
            if (data.success && data.fromCache && Array.isArray(data.articles) && data.articles.length > 0) {
                setNews(decorateArticles(data.articles));
                setLastUpdate(data.fetched_at ? new Date(data.fetched_at) : new Date());
                setError(null);
                setInfoMessage(null);
                return true;
            }
        } catch (e) {
            console.warn('[HomePage] No se pudo leer cache de noticias:', e);
        }
        return false;
    }, [user]);

    // Búsqueda ad-hoc para una fecha específica que el usuario filtró pero
    // que no tiene noticias en el pool actual. Las noticias resultantes se
    // mergean al pool (`news`) sin duplicar por título. No se persiste cache.
    const searchByDate = useCallback(async (targetDate) => {
        if (!targetDate || keywords.length === 0) return;
        // No re-disparar si ya buscamos esta fecha en esta sesión.
        // El ref es síncrono: marcamos antes del await para que llamadas
        // concurrentes vean el flag inmediatamente y se frenen.
        if (searchedDatesRef.current.has(targetDate)) return;
        searchedDatesRef.current.add(targetDate);

        setDateSearching(true);

        try {
            const queries = keywords.map(k => `"${k}"`);
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 45000);

            const response = await fetch(MEDIA_NEWS_SEARCH_URL, {
                method: 'POST',
                headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
                signal: controller.signal,
                body: JSON.stringify({
                    queries,
                    keywords,
                    username: user?.username,
                    targetDate,
                    country,
                })
            });
            clearTimeout(timeoutId);

            if (!response.ok) return;
            const data = await response.json();

            if (data.success && Array.isArray(data.articles) && data.articles.length > 0) {
                // Mergear al pool sin duplicar (por título).
                const decorated = decorateArticles(data.articles);
                setNews(prev => {
                    const existingTitles = new Set(prev.map(a => a.title));
                    const fresh = decorated.filter(a => !existingTitles.has(a.title));
                    return [...prev, ...fresh];
                });
            }
        } catch (e) {
            console.warn('[HomePage] Error en búsqueda por fecha:', e);
        } finally {
            setDateSearching(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keywords, user, country]);

    // Búsqueda fresh contra SerpAPI (vía nuestro backend). Llamada:
    //   - automáticamente al cargar si no hay cache fresco del día
    //   - manualmente cuando el usuario hace click en "Actualizar"
    //   - automáticamente cuando el usuario cambia de país (pasa override
    //     explícito porque setCountry aún no se refleja en el closure)
    const fetchRetailNews = async (countryOverride) => {
        if (keywords.length === 0) {
            setError('Configura al menos una keyword para buscar noticias.');
            return;
        }
        const useCountry = countryOverride || country;

        try {
            setLoading(true);
            setError(null);
            setInfoMessage(null);
            trackAction('refresh_news', {
                keywords_count: keywords.length,
                country: useCountry,
                country_override: Boolean(countryOverride),
            });

            // 1 keyword = 1 query independiente. El backend las ejecuta en
            // paralelo (con un semáforo de concurrencia) y combina los
            // resultados. Cada keyword tiene su propia oportunidad de traer
            // noticias, así que si una arroja 0 las otras siguen funcionando.
            const queries = keywords.map(k => `"${k}"`);

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 45000);

            const response = await fetch(MEDIA_NEWS_SEARCH_URL, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                signal: controller.signal,
                body: JSON.stringify({
                    queries,
                    keywords,
                    username: user?.username,
                    country: useCountry,
                })
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({
                    error: `HTTP ${response.status}: ${response.statusText}`
                }));
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }

            const data = await response.json();

            if (data.success && data.articles && data.articles.length > 0) {
                setNews(decorateArticles(data.articles));
                setLastUpdate(new Date());
            } else if (data.success === false) {
                // El backend no trajo resultados nuevos. Si ya había noticias
                // en pantalla, las mantenemos y mostramos un aviso suave.
                // Si la pantalla estaba vacía, sí mostrar como error.
                if (news.length > 0) {
                    setInfoMessage(data.message || 'Sin noticias nuevas en esta búsqueda.');
                } else {
                    setError(data.message || 'No se encontraron noticias relevantes.');
                }
            } else {
                throw new Error(data.error || 'Respuesta inesperada');
            }

        } catch (error) {
            console.error('Error obteniendo noticias:', error);
            if (error.name === 'AbortError') {
                setError('La búsqueda tardó demasiado tiempo. Intenta de nuevo.');
            } else {
                setError(`Error: ${error.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    // Efectos
    useEffect(() => {
        loadKeywords();
        loadPresets();
        loadCountry();
        // Sub-módulos TikTok: solo cargamos cache si el usuario lo tiene habilitado.
        // Destacados (saved) los cargamos siempre para que el tab 'saved' funcione
        // aunque el usuario solo tenga noticias activas.
        if (tiktokEnabled) {
            loadTiktok();
        }
        loadSavedTiktoks();
    }, [loadKeywords, loadPresets, loadCountry, loadTiktok, loadSavedTiktoks, tiktokEnabled]);

    // Polling de TikTok: mientras esté pending, golpea /status cada 30s.
    // Cuando cambia a ready, recarga el snapshot completo (con los posts).
    // Si cambia a failed, deja el error visible.
    useEffect(() => {
        if (tiktokStatus !== 'pending' || !user?.username) return;
        let cancelled = false;
        const tick = async () => {
            try {
                const res = await fetch(
                    `${STRANDS_API_URL}/api/media-social/status?username=${encodeURIComponent(user.username)}&platform=tiktok`
                );
                if (!res.ok) return;
                const data = await res.json();
                if (cancelled) return;
                if (data.status === 'ready') {
                    await loadTiktok();
                } else if (data.status === 'failed') {
                    setTiktokStatus('failed');
                    setTiktokError(data.error || 'El proveedor reportó error');
                }
                // si sigue pending, el interval vuelve a disparar
            } catch (e) {
                // Silencio: el próximo tick retoma.
            }
        };
        const id = setInterval(tick, 30000);
        return () => { cancelled = true; clearInterval(id); };
    }, [tiktokStatus, user?.username, loadTiktok]);

    // Auto-refresh: si el último snapshot tiene >TIKTOK_STALE_DAYS días Y la
    // tab activa es TikTok, dispara uno nuevo en background. El umbral es
    // suficiente para no quemar llamadas al proveedor; el usuario puede
    // forzar siempre con el botón Actualizar.
    useEffect(() => {
        if (!tiktokEnabled) return;
        if (activeTab !== 'tiktok') return;
        if (tiktokStatus === 'pending') return;
        if (!user?.username || tiktokRefreshing) return;
        const isIdle = tiktokStatus === 'idle' && !tiktokFetchedAt;
        let isStale = false;
        if (tiktokFetchedAt) {
            const ageDays = (Date.now() - new Date(tiktokFetchedAt).getTime()) / 86400000;
            isStale = ageDays > TIKTOK_STALE_DAYS;
        }
        if (isIdle || isStale) {
            refreshTiktok();
        }
    }, [activeTab, tiktokStatus, tiktokFetchedAt, user?.username, tiktokRefreshing, refreshTiktok]);

    // Carga inicial cache-first: intenta leer cache del día; si no hay,
    // dispara búsqueda fresca. Esperamos a tener el país cargado para no
    // disparar una búsqueda con el default y luego refetch al llegar la
    // preferencia real (duplicaría llamadas a SerpAPI).
    useEffect(() => {
        if (!newsEnabled) return;
        if (keywordsLoading || !user?.username || keywords.length === 0) return;
        if (!countryLoaded) return;
        if (news.length > 0 || loading) return;

        (async () => {
            const usedCache = await loadCachedNews();
            if (!usedCache) {
                fetchRetailNews();
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keywordsLoading, user?.username, countryLoaded, newsEnabled]);

    // Si el sub-módulo del tab activo se deshabilita (o las features llegan
    // tarde tras el primer render), saltamos al primer tab disponible.
    useEffect(() => {
        if (activeTab === 'feed' && !newsEnabled) {
            setActiveTab(pickInitialTab());
        } else if (activeTab === 'tiktok' && !tiktokEnabled) {
            setActiveTab(pickInitialTab());
        }
    }, [newsEnabled, tiktokEnabled, activeTab, pickInitialTab]);

    // Helper para chequear si una noticia coincide con el filtro de fecha
    // activo (si no hay filtro, todas coinciden). Se usa también para
    // recalcular los conteos del select.
    const matchesDateFilter = useCallback((article) => {
        if (!filterDate) return true;
        if (!article.publishedAt) return false;
        try {
            const d = new Date(article.publishedAt);
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}` === filterDate;
        } catch {
            return false;
        }
    }, [filterDate]);

    // Pool de noticias del feed que pasa el filtro de fecha (si hay).
    // El select se construye sobre este subconjunto para que sus conteos
    // reflejen lo realmente visible al elegir cada opción.
    const newsWithinDate = useMemo(
        () => news.filter(matchesDateFilter),
        [news, matchesDateFilter]
    );

    // Conteo de noticias por keyword usando queryIndex: cada noticia pertenece
    // a UNA sola keyword (la que la trajo de la búsqueda), así los conteos
    // son disjuntos y suman al total. Los conteos respetan el filtro de
    // fecha activo (si seleccionas un día, solo cuentan las de ese día).
    const newsCountByKeyword = useMemo(() => {
        const counts = {};
        keywords.forEach((k, i) => {
            const expectedIndex = i + 1; // queryIndex es 1-based en el backend
            counts[k] = newsWithinDate.filter(a => a.queryIndex === expectedIndex).length;
        });
        return counts;
    }, [keywords, newsWithinDate]);

    // Pool de artículos según la tab activa (feed = noticias del mes;
    // saved = destacadas persistentes del usuario).
    const tabArticles = activeTab === 'saved' ? savedArticles : news;

    // Lista filtrada según keyword y fecha. La tab 'saved' ignora el filtro
    // por keyword (las destacadas no necesariamente vienen de un queryIndex
    // alineado con las keywords actuales), pero sí respeta el filtro de fecha.
    const filteredNews = useMemo(() => {
        // Empezamos siempre por el pool de la tab. En Feed aplicamos fecha
        // primero (reusamos newsWithinDate cuando es feed para coherencia
        // con los conteos del select); en Saved aplicamos fecha solo si está
        // activa (la UI la oculta pero por defensa).
        let pool;
        if (activeTab === 'feed') {
            pool = newsWithinDate;
        } else {
            pool = tabArticles;
        }

        if (activeTab === 'feed' && filterKeyword) {
            const idx = keywords.indexOf(filterKeyword);
            if (idx === -1) {
                pool = [];
            } else {
                const expectedIndex = idx + 1;
                pool = pool.filter(a => a.queryIndex === expectedIndex);
            }
        }

        return pool;
    }, [tabArticles, activeTab, filterKeyword, keywords, newsWithinDate]);

    const totalPages = Math.max(1, Math.ceil(filteredNews.length / NEWS_PAGE_SIZE));
    const safePage = Math.min(currentPage, totalPages);
    const paginatedNews = useMemo(
        () => filteredNews.slice((safePage - 1) * NEWS_PAGE_SIZE, safePage * NEWS_PAGE_SIZE),
        [filteredNews, safePage]
    );

    // Reset de página cuando cambia cualquier filtro, la tab o llega data nueva.
    useEffect(() => {
        setCurrentPage(1);
    }, [filterKeyword, filterDate, activeTab, news, savedArticles]);

    // Carga inicial de destacadas al tener usuario.
    useEffect(() => {
        if (user?.username) loadSavedNews();
    }, [user?.username, loadSavedNews]);

    // Cuando el usuario filtra el Feed por una fecha, dispara siempre una
    // búsqueda específica a SerpAPI para ese día (el ref searchedDatesRef
    // previene re-buscar la misma fecha dentro de la sesión). Antes solo
    // buscábamos cuando el pool quedaba vacío, pero el usuario quiere que
    // se vea la búsqueda real corriendo.
    useEffect(() => {
        if (activeTab !== 'feed') return;
        if (!filterDate) return;
        if (loading || dateSearching) return;
        if (news.length === 0) return; // Espera a que el feed inicial cargue

        searchByDate(filterDate);
    }, [activeTab, filterDate, news.length, loading, dateSearching, searchByDate]);

    // Formatear fecha
    const formatDate = (dateString) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('es-MX', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'Hace poco';
        }
    };

    const getDaysAgoText = (daysAgo) => {
        if (daysAgo === 0) return t('homepage.today');
        if (daysAgo === 1) return t('homepage.yesterday');
        return `${daysAgo} ${t('homepage.daysAgo')}`;
    };

    // Compacta números grandes a 1.2K / 3.4M para las métricas de TikTok.
    const formatCompact = (n) => {
        const v = Number(n) || 0;
        if (v < 1000) return String(v);
        if (v < 1_000_000) return `${(v / 1000).toFixed(v < 10_000 ? 1 : 0).replace(/\.0$/, '')}K`;
        if (v < 1_000_000_000) return `${(v / 1_000_000).toFixed(v < 10_000_000 ? 1 : 0).replace(/\.0$/, '')}M`;
        return `${(v / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && newKeyword.trim()) {
            addKeyword(newKeyword);
        }
    };

    return (
        <main className="homepage-main">
            <div className="homepage">
                {/* Header Section */}
                <div className="header-section">
                    <div className="header-top">
                        <div className="header-title-area">
                            <div>
                                <h1 className="main-title">{t('homepage.title')}</h1>
                                <p className="subtitle">
                                    {t('homepage.subtitle')}
                                </p>
                            </div>
                        </div>
                    </div>

                </div>

                {/* Combined Status & Keywords Bar */}
                {!showKeywordsPanel && keywords.length > 0 && (
                    <div className="combined-status-bar">
                        <div className="status-left">
                            <div className="keywords-preview-section">
                                <FaTags className="keywords-icon" />
                                <div className="keywords-preview">
                                    {keywords.slice(0, 4).map((kw, idx) => (
                                        <span key={idx} className="keyword-preview-tag">{kw}</span>
                                    ))}
                                    {keywords.length > 4 && (
                                        <span className="keywords-more">+{keywords.length - 4}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            className="edit-keywords-btn"
                            onClick={() => setShowKeywordsPanel(true)}
                        >
                            <FaCog />
                            {t('homepage.customize')}
                        </button>
                    </div>
                )}

                {/* Keywords Configuration Panel */}
                {showKeywordsPanel && (
                    <div className="keywords-panel">
                        <div className="keywords-panel-header">
                            <div className="panel-title-area">
                                <FaTags className="panel-icon" />
                                <div>
                                    <h2>{t('homepage.keywordsConfig')}</h2>
                                    <p>{t('homepage.keywordsConfigDesc')}</p>
                                </div>
                            </div>
                            <button
                                className="close-panel-btn"
                                onClick={() => setShowKeywordsPanel(false)}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        {/* Input de nueva keyword */}
                        <div className="keyword-input-section">
                            <div className="keyword-input-wrapper">
                                <FaSearch className="input-icon" />
                                <input
                                    ref={keywordInputRef}
                                    type="text"
                                    placeholder={keywords.length >= 20 ? t('homepage.keywordsLimitReached') : t('homepage.searchPlaceholder')}
                                    value={newKeyword}
                                    onChange={(e) => {
                                        setNewKeyword(e.target.value);
                                        if (keywordsLimitError) setKeywordsLimitError('');
                                    }}
                                    onKeyPress={handleKeyPress}
                                    className="keyword-input"
                                    disabled={keywords.length >= 20}
                                />
                                {keywords.length < 20 && (
                                    <button
                                        className="add-keyword-btn"
                                        onClick={() => addKeyword(newKeyword)}
                                        disabled={!newKeyword.trim()}
                                    >
                                        <FaPlus />
                                    </button>
                                )}
                            </div>
                            {keywordsLimitError && (
                                <div className="keywords-limit-error">
                                    <FaInfoCircle />
                                    <span>{keywordsLimitError}</span>
                                </div>
                            )}
                        </div>

                        {/* Presets Section */}
                        <div className="presets-section">
                            <button
                                className="presets-toggle"
                                onClick={() => setShowPresets(!showPresets)}
                            >
                                <FaIndustry />
                                <span>{t('homepage.useIndustryTemplate')}</span>
                                {showPresets ? <FaChevronUp /> : <FaChevronDown />}
                            </button>

                            {showPresets && (
                                <div className="presets-grid">
                                    {Object.entries(presets).map(([key, preset]) => (
                                        <button
                                            key={key}
                                            className={`preset-card ${selectedPreset === key ? 'selected' : ''}`}
                                            onClick={() => applyPreset(key)}
                                        >
                                            <span className="preset-name">{preset.name}</span>
                                            <span className="preset-count">
                                                {preset.keywords.length} keywords
                                            </span>
                                            {selectedPreset === key && <FaCheck className="preset-check" />}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Keywords List */}
                        <div className="keywords-list-section">
                            <div className="keywords-list-header">
                                <h3>
                                    <FaClipboardList />
                                    {t('homepage.activeKeywords')} ({keywords.length})
                                </h3>
                                <div className="keywords-list-actions">
                                    {keywords.length > 0 && (
                                        <button
                                            className="clear-all-btn"
                                            onClick={clearAllKeywords}
                                        >
                                            <FaTrash />
                                            {t('homepage.clearAll')}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {keywordsLoading ? (
                                <div className="keywords-loading">
                                    <div className="loading-spinner-small"></div>
                                    <span>{t('homepage.loadingKeywords')}</span>
                                </div>
                            ) : keywords.length === 0 ? (
                                <div className="keywords-empty">
                                    <FaInfoCircle />
                                    <p>{t('homepage.noKeywordsConfigured')}</p>
                                    <p className="hint">{t('homepage.addKeywordsHint')}</p>
                                </div>
                            ) : (
                                <div className="keywords-tags">
                                    {keywords.map((keyword, idx) => (
                                        <div key={`${keyword}-${idx}`} className="keyword-tag">
                                            <span>{keyword}</span>
                                            <button
                                                className="remove-keyword-btn"
                                                onClick={() => removeKeyword(keyword)}
                                            >
                                                <FaTimes />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Save Section */}
                        <div className="keywords-save-section">
                            {keywordsLastUpdated && (
                                <span className="last-saved">
                                    {t('homepage.lastSaved')} {formatDate(keywordsLastUpdated)}
                                </span>
                            )}
                            <button
                                className={`save-keywords-btn ${hasChanges ? 'has-changes' : ''}`}
                                onClick={saveKeywords}
                                disabled={keywordsSaving || !hasChanges}
                            >
                                {keywordsSaving ? (
                                    <>
                                        <FaSync className="spinning" />
                                        <span>{t('homepage.saving')}</span>
                                    </>
                                ) : (
                                    <>
                                        <FaSync />
                                        <span>{t('homepage.saveConfig')}</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* Loading State */}
                {loading && (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <div className="loading-title">{t('homepage.searchingNews')}</div>
                        <div className="loading-subtitle">
                            {t('homepage.analyzingKeywords', { count: keywords.length })}
                        </div>
                        <div className="loading-note">
                            {t('homepage.searchMayTake')}
                        </div>
                    </div>
                )}

                {/* Error State (cuando no hay noticias en pantalla) */}
                {error && !loading && (
                    <div className="error-container">
                        <FaInfoCircle />
                        <span>{error}</span>
                    </div>
                )}

                {/* Info State (refresh sin novedades, pero hay noticias previas) */}
                {infoMessage && !loading && !error && (
                    <div className="info-container">
                        <FaInfoCircle />
                        <span>{infoMessage}</span>
                    </div>
                )}

                {/* News Section: Feed | TikTok | Destacadas */}
                {!loading && (news.length > 0 || savedArticles.length > 0 || tiktokPosts.length > 0 || tiktokStatus !== 'idle' || savedTiktoks.length > 0) && (
                    <div className="news-section">
                        {/* Tabs (filtradas por sub-módulos habilitados) */}
                        <div className="news-tabs">
                            {newsEnabled && (
                                <button
                                    className={`news-tab ${activeTab === 'feed' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('feed')}
                                >
                                    <FaNewspaper />
                                    <span>{t('homepage.tabNews')}</span>
                                    <span className="news-tab-count">
                                        {filterDate || filterKeyword ? filteredNews.length : news.length}
                                    </span>
                                </button>
                            )}
                            {tiktokEnabled && (
                                <button
                                    className={`news-tab ${activeTab === 'tiktok' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('tiktok')}
                                >
                                    <FaVideo />
                                    <span>TikTok</span>
                                    {tiktokStatus === 'pending' ? (
                                        <FaSync className="news-tab-spinner spinning" />
                                    ) : (
                                        <span className="news-tab-count">{tiktokPosts.length}</span>
                                    )}
                                </button>
                            )}
                            <button
                                className={`news-tab ${activeTab === 'saved' ? 'active' : ''}`}
                                onClick={() => setActiveTab('saved')}
                            >
                                <FaStar />
                                <span>{t('homepage.tabSaved')}</span>
                                <span className="news-tab-count">{savedArticles.length + savedTiktoks.length}</span>
                            </button>
                        </div>

                        <div className="news-section-header">
                            <div className="news-section-header-left">
                                <h2>
                                    {activeTab === 'feed' && <FaNewspaper />}
                                    {activeTab === 'tiktok' && <FaVideo />}
                                    {activeTab === 'saved' && <FaStar />}
                                    {activeTab === 'feed' && t('homepage.relevantNews')}
                                    {activeTab === 'tiktok' && t('homepage.tiktokTitle')}
                                    {activeTab === 'saved' && t('homepage.savedTitle')}
                                    {activeTab === 'feed' && lastUpdate && !loading && (
                                        <span className="news-section-title-date">
                                            <FaCalendarAlt />
                                            <span>{formatDate(lastUpdate)}</span>
                                        </span>
                                    )}
                                    {activeTab === 'tiktok' && tiktokFetchedAt && (
                                        <span className="news-section-title-date">
                                            <FaCalendarAlt />
                                            <span>{formatDate(tiktokFetchedAt)}</span>
                                        </span>
                                    )}
                                </h2>

                                {activeTab === 'saved' && (savedArticles.length + savedTiktoks.length) > 0 && (
                                    <div className="news-section-meta">
                                        <span className="news-section-meta-item">
                                            <FaStar />
                                            <span>{savedArticles.length + savedTiktoks.length} {t('homepage.tabSaved').toLowerCase()}</span>
                                        </span>
                                    </div>
                                )}
                            </div>

                            <div className="news-filters-row">
                                {activeTab === 'feed' && (
                                    <button
                                        className="section-refresh-btn"
                                        onClick={() => fetchRetailNews()}
                                        disabled={loading || keywords.length === 0}
                                        title={t('homepage.update')}
                                    >
                                        <FaSync className={loading ? 'spinning' : ''} />
                                        <span>
                                            {loading ? t('homepage.searching') : t('homepage.update')}
                                        </span>
                                    </button>
                                )}
                                {activeTab === 'tiktok' && (
                                    <>
                                        {tiktokPosts.length > 0 && (
                                            <div className="news-filter">
                                                <label htmlFor="tiktok-keyword-select">Keyword:</label>
                                                <select
                                                    id="tiktok-keyword-select"
                                                    className="news-filter-select"
                                                    value={tiktokKeywordFilter}
                                                    onChange={(e) => setTiktokKeywordFilter(e.target.value)}
                                                >
                                                    <option value="">Todas ({tiktokPosts.length})</option>
                                                    {Object.entries(tiktokCountByKeyword)
                                                        .sort((a, b) => b[1] - a[1])
                                                        .map(([k, count]) => (
                                                            <option key={k} value={k}>
                                                                {k.replace(/^#/, '')} ({count})
                                                            </option>
                                                        ))}
                                                </select>
                                            </div>
                                        )}
                                        {tiktokPosts.length > 0 && (
                                            <div className="news-filter">
                                                <label htmlFor="tiktok-sort-select">
                                                    {t('homepage.tiktokSortLabel')}:
                                                </label>
                                                <select
                                                    id="tiktok-sort-select"
                                                    className="news-filter-select"
                                                    value={tiktokSortBy}
                                                    onChange={(e) => setTiktokSortBy(e.target.value)}
                                                >
                                                    <option value="views">{t('homepage.tiktokSortViews')}</option>
                                                    <option value="recent">{t('homepage.tiktokSortRecent')}</option>
                                                    <option value="oldest">{t('homepage.tiktokSortOldest')}</option>
                                                    <option value="likes">{t('homepage.tiktokSortLikes')}</option>
                                                    <option value="comments">{t('homepage.tiktokSortComments')}</option>
                                                </select>
                                            </div>
                                        )}
                                        <button
                                            className="section-refresh-btn"
                                            onClick={refreshTiktok}
                                            disabled={tiktokRefreshing || tiktokStatus === 'pending' || keywords.length === 0}
                                        >
                                            <FaSync className={tiktokStatus === 'pending' ? 'spinning' : ''} />
                                            <span>
                                                {tiktokStatus === 'pending'
                                                    ? t('homepage.tiktokSearching')
                                                    : t('homepage.tiktokRefresh')}
                                            </span>
                                        </button>
                                    </>
                                )}
                                {activeTab === 'feed' && (
                                    <>
                                        <div className="news-filter">
                                            <label htmlFor="news-filter-select">Keyword:</label>
                                            <select
                                                id="news-filter-select"
                                                className="news-filter-select"
                                                value={filterKeyword}
                                                onChange={(e) => setFilterKeyword(e.target.value)}
                                            >
                                                <option value="">Todas las noticias ({newsWithinDate.length})</option>
                                                {keywords
                                                    .map(k => ({ k, count: newsCountByKeyword[k] || 0 }))
                                                    .filter(({ count }) => count > 0)
                                                    .sort((a, b) => b.count - a.count)
                                                    .map(({ k, count }) => (
                                                        <option key={k} value={k}>{k} ({count})</option>
                                                    ))}
                                            </select>
                                        </div>
                                        <div className="news-filter">
                                            <label htmlFor="news-filter-date">Fecha:</label>
                                            <input
                                                id="news-filter-date"
                                                type="date"
                                                className="news-filter-date"
                                                value={filterDate}
                                                max={(() => {
                                                    const d = new Date();
                                                    const y = d.getFullYear();
                                                    const m = String(d.getMonth() + 1).padStart(2, '0');
                                                    const day = String(d.getDate()).padStart(2, '0');
                                                    return `${y}-${m}-${day}`;
                                                })()}
                                                onChange={(e) => setFilterDate(e.target.value)}
                                            />
                                            {filterDate && (
                                                <button
                                                    className="news-filter-clear"
                                                    onClick={() => setFilterDate('')}
                                                    title="Quitar filtro de fecha"
                                                >
                                                    <FaTimes />
                                                </button>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Spinner inline: buscando noticias para la fecha filtrada */}
                        {activeTab === 'feed' && dateSearching && (
                            <div className="news-date-searching">
                                <FaSync className="spinning" />
                                <span>Buscando noticias del {filterDate}...</span>
                            </div>
                        )}

                        {/* === TIKTOK TAB === */}
                        {activeTab === 'tiktok' && (
                            <>
                                {tiktokStatus === 'pending' && (
                                    <div className="tiktok-pending">
                                        <FaSync className="spinning" />
                                        <div className="tiktok-pending-text">
                                            <strong>{t('homepage.tiktokPendingTitle')}</strong>
                                            <span>{t('homepage.tiktokPendingHint')}</span>
                                            {tiktokTriggeredAt && (
                                                <span className="tiktok-pending-time">
                                                    {t('homepage.tiktokStartedAt')} {formatDate(tiktokTriggeredAt)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {tiktokStatus === 'failed' && tiktokError && (
                                    <div className="error-container">
                                        <FaInfoCircle />
                                        <span>{tiktokError}</span>
                                    </div>
                                )}

                                {tiktokStatus !== 'pending' && tiktokPosts.length === 0 && (
                                    <div className="news-empty-filtered">
                                        {keywords.length === 0
                                            ? t('homepage.tiktokEmptyNoKeywords')
                                            : t('homepage.tiktokEmpty')}
                                    </div>
                                )}

                                {tiktokPosts.length > 0 && sortedTiktokPosts.length === 0 && (
                                    <div className="news-empty-filtered">
                                        No hay TikToks para la keyword "{tiktokKeywordFilter.replace(/^#/, '')}".
                                    </div>
                                )}

                                {sortedTiktokPosts.length > 0 && (
                                    <>
                                        <div className="tiktok-grid">
                                            {sortedTiktokPosts.map((post, index) => (
                                                <TikTokCard
                                                    key={post.id || `tiktok-${index}`}
                                                    post={post}
                                                    index={index}
                                                    formatCompact={formatCompact}
                                                    isSaved={savedTiktokUrls.has(post.url)}
                                                    onToggleSave={toggleSaveTiktok}
                                                    onDelete={hideTiktok}
                                                    onOpen={openCommentsModal}
                                                    t={t}
                                                />
                                            ))}
                                        </div>
                                    </>
                                )}
                            </>
                        )}

                        {/* === FEED / DESTACADAS === */}
                        {/* Empty state cuando una tab/filtro queda vacío. En Destacadas
                            solo aparece si NO hay ni noticias ni TikToks destacados. */}
                        {activeTab !== 'tiktok' && !dateSearching && paginatedNews.length === 0 &&
                         !(activeTab === 'saved' && savedTiktoks.length > 0) && (
                            <div className="news-empty-filtered">
                                {activeTab === 'saved'
                                    ? 'No tienes destacados todavía. Marca con la estrella las noticias o TikToks que quieras guardar.'
                                    : filterDate
                                        ? `No se encontraron noticias para el ${filterDate}.`
                                        : 'No hay noticias que coincidan con el filtro.'}
                            </div>
                        )}

                        {activeTab !== 'tiktok' && <div className="news-grid">
                            {paginatedNews.map((article, index) => (
                                <div
                                    key={`${article.title}-${index}`}
                                    className={`news-card fade-in-up ${!article.url ? 'disabled' : ''}`}
                                    style={{ animationDelay: `${index * 0.05}s` }}
                                    onClick={() => article.url && window.open(article.url, '_blank')}
                                >
                                    {/* Image Container */}
                                    <div className="news-image-container">
                                        <img
                                            className="news-image"
                                            src={article.image}
                                            alt={article.title}
                                            onError={(e) => {
                                                e.target.src = `https://via.placeholder.com/320x160/073C5C/ffffff?text=News`;
                                            }}
                                        />

                                        {article.daysAgo !== undefined && (
                                            <div className="image-overlay-date">
                                                {getDaysAgoText(article.daysAgo)}
                                            </div>
                                        )}

                                        {article.url && (
                                            <button
                                                className={`news-save-btn ${savedUrls.has(article.url) ? 'saved' : ''}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleSaveArticle(article);
                                                }}
                                                title={savedUrls.has(article.url) ? 'Quitar de destacadas' : 'Destacar noticia'}
                                            >
                                                {savedUrls.has(article.url) ? <FaStar /> : <FaRegStar />}
                                            </button>
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div className="news-content">
                                        {/* Source and Date */}
                                        <div className="news-meta">
                                            <span className="news-source">
                                                {article.source}
                                            </span>
                                            <span className="news-date">
                                                {formatDate(article.publishedAt)}
                                            </span>
                                        </div>

                                        {/* Title */}
                                        <h3 className="news-title">
                                            {article.title}
                                        </h3>

                                        {/* Footer */}
                                        <div className="news-footer">
                                            <div className={`news-read-link ${!article.url ? 'disabled' : ''}`}>
                                                {!article.url ? (
                                                    t('homepage.noLink')
                                                ) : (
                                                    <>
                                                        <FaExternalLinkAlt />
                                                        {t('homepage.readArticle')}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>}

                        {/* TikToks destacados dentro de tab Destacadas */}
                        {activeTab === 'saved' && savedTiktoks.length > 0 && (
                            <div className="saved-tiktoks-section">
                                <h3 className="saved-tiktoks-header">
                                    <FaVideo />
                                    <span>{t('homepage.savedTiktoksHeader')} ({savedTiktoks.length})</span>
                                </h3>
                                <div className="tiktok-grid">
                                    {savedTiktoks.map((post, index) => (
                                        <TikTokCard
                                            key={post.id || post.url || `saved-tt-${index}`}
                                            post={post}
                                            index={index}
                                            formatCompact={formatCompact}
                                            isSaved={true}
                                            onToggleSave={toggleSaveTiktok}
                                            onOpen={openCommentsModal}
                                            t={t}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {activeTab !== 'tiktok' && totalPages > 1 && (
                            <div className="news-pagination">
                                <button
                                    className="pagination-btn"
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={safePage === 1}
                                >
                                    ← Anterior
                                </button>
                                <span className="pagination-info">
                                    Página {safePage} de {totalPages} · {filteredNews.length} noticias
                                </span>
                                <button
                                    className="pagination-btn"
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={safePage === totalPages}
                                >
                                    Siguiente →
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Empty News State */}
                {!loading && news.length === 0 && keywords.length > 0 && !error && (
                    <div className="empty-state">
                        <div className="empty-icon">📰</div>
                        <div className="empty-title">
                            {t('homepage.noNewsFound')}
                        </div>
                        <div className="empty-subtitle">
                            {t('homepage.tryOtherKeywords')}
                        </div>
                        <button className="btn-primary" onClick={() => fetchRetailNews()}>
                            <FaSync />
                            {t('homepage.searchNews')}
                        </button>
                    </div>
                )}

                {/* Modal de comentarios de TikTok */}
                {activeCommentsPost && (
                    <TikTokCommentsModal
                        post={activeCommentsPost}
                        onClose={() => setActiveCommentsPost(null)}
                        t={t}
                        formatCompact={formatCompact}
                        formatDate={formatDate}
                        apiUrl={STRANDS_API_URL}
                        isSaved={savedTiktokUrls.has(activeCommentsPost.url)}
                        onSave={toggleSaveTiktok}
                        sentimentEnabled={sentimentEnabled}
                        username={user?.username}
                        trackAction={trackAction}
                    />
                )}
            </div>
        </main>
    );
};

export default HomePage;
